import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { name: 'How It Works', href: '#features' },
  { name: 'Features', href: '/features' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Use Cases', href: '/use-cases' },
  { name: 'Compare', href: '/compare' },
  { name: 'Docs', href: '/docs' },
];

export default function MorphingNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLinkClick = () => setMobileMenuOpen(false);

  return (
    <div
      className="sticky top-4 z-50 flex justify-center px-4"
      data-testid="morphing-navbar-wrapper"
    >
      <nav
        className="transition-all duration-500 ease-out overflow-visible"
        style={{
          maxWidth: isScrolled ? '375px' : '1400px',
          width: '100%',
          background: isScrolled
            ? 'rgba(26, 26, 26, 0.4)'
            : 'rgba(10, 10, 10, 0.95)',
          backdropFilter: isScrolled
            ? 'blur(20px) saturate(180%)'
            : 'blur(12px)',
          WebkitBackdropFilter: isScrolled
            ? 'blur(20px) saturate(180%)'
            : 'blur(12px)',
          border: isScrolled
            ? '1px solid rgba(255,255,255,0.2)'
            : '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: isScrolled ? '6px 12px' : '16px 24px',
          boxShadow: isScrolled
            ? '0 10px 25px -5px rgba(0,0,0,0.3), 0 8px 10px -6px rgba(0,0,0,0.2)'
            : 'none',
        }}
        data-testid="morphing-navbar"
      >
        <div className="flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/"
              className="flex items-center gap-2 flex-shrink-0"
              data-testid="navbar-logo"
              onClick={handleLinkClick}
            >
              <img
                src="/edrano-logo-dark.png"
                alt="Edrano"
                className="transition-all duration-500 rounded-lg"
                style={{
                  width: isScrolled ? '30px' : '36px',
                  height: isScrolled ? '30px' : '36px',
                }}
              />
              {!isScrolled && (
                <span className="text-white font-semibold text-sm hidden sm:inline">
                  Edrano
                </span>
              )}
            </Link>
          </div>

          {/* Center: Desktop nav links — hidden when scrolled */}
          {!isScrolled && (
            <div className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map((link) =>
                link.href.startsWith('/') ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-sm font-medium transition-colors"
                    style={{ color: '#9CA3AF' }}
                    onMouseEnter={(e) => (e.target.style.color = '#FFFFFF')}
                    onMouseLeave={(e) => (e.target.style.color = '#9CA3AF')}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-sm font-medium transition-colors"
                    style={{ color: '#9CA3AF' }}
                    onMouseEnter={(e) => (e.target.style.color = '#FFFFFF')}
                    onMouseLeave={(e) => (e.target.style.color = '#9CA3AF')}
                  >
                    {link.name}
                  </a>
                )
              )}
            </div>
          )}

          {/* Right: Burger + CTA */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Burger for mobile (default) OR desktop when scrolled */}
            <button
              className={`p-1.5 rounded-lg transition-colors hover:bg-white/10 ${
                isScrolled ? 'flex' : 'flex md:hidden'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="navbar-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className={`text-white ${isScrolled ? 'w-4 h-4' : 'w-5 h-5'}`} />
              ) : (
                <Menu className={`text-white ${isScrolled ? 'w-4 h-4' : 'w-5 h-5'}`} />
              )}
            </button>

            {/* CTA Button */}
            <Link
              to="/login"
              className="font-semibold rounded-lg transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap"
              style={{
                background: '#F4C430',
                color: '#1A1A1A',
                padding: isScrolled ? '4px 10px' : '10px 20px',
                fontSize: isScrolled ? '11px' : '14px',
              }}
              onMouseEnter={(e) => (e.target.style.background = '#FFD93D')}
              onMouseLeave={(e) => (e.target.style.background = '#F4C430')}
              data-testid="navbar-login-cta"
            >
              Login
            </Link>
          </div>
        </div>

        {/* Dropdown Menu */}
        {mobileMenuOpen && (
          <div
            className="mt-3 rounded-xl overflow-hidden"
            style={{
              background: 'rgba(26, 26, 26, 0.85)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(255,255,255,0.1)',
              minWidth: '280px',
              position: isScrolled ? 'absolute' : 'relative',
              left: isScrolled ? '0' : 'auto',
              right: isScrolled ? '0' : 'auto',
              top: isScrolled ? '100%' : 'auto',
              marginTop: isScrolled ? '8px' : '12px',
            }}
            data-testid="navbar-mobile-menu"
          >
            <div className="py-3 px-4 space-y-1">
              {NAV_LINKS.map((link) =>
                link.href.startsWith('/') ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={handleLinkClick}
                    className="block py-2.5 px-3 text-sm font-medium rounded-lg transition-colors"
                    style={{ color: '#9CA3AF' }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#FFFFFF';
                      e.target.style.background = 'rgba(255,255,255,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#9CA3AF';
                      e.target.style.background = 'transparent';
                    }}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={handleLinkClick}
                    className="block py-2.5 px-3 text-sm font-medium rounded-lg transition-colors"
                    style={{ color: '#9CA3AF' }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#FFFFFF';
                      e.target.style.background = 'rgba(255,255,255,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#9CA3AF';
                      e.target.style.background = 'transparent';
                    }}
                  >
                    {link.name}
                  </a>
                )
              )}
              <hr className="border-white/10 my-2" />
              <Link
                to="/login"
                onClick={handleLinkClick}
                className="block py-2.5 px-3 text-sm font-medium rounded-lg text-center transition-colors"
                style={{ background: '#F4C430', color: '#1A1A1A' }}
              >
                Login
              </Link>
            </div>
          </div>
        )}
      </nav>
    </div>
  );
}
